<template>
  <div>
    <div class="sticky top-0 z-10 hidden mx-auto mb-8 bg-white border-b band max-w-site border-brand-3px-4 lg:mb-16 xl:mb-20 md:block">
      <div class="px-4 py-2 mx-auto band__content max-w-content-large">
        <Progress :step="step" :reservation="reservation" v-on:setstep="setStep" />
      </div>
    </div>
    <div class="mx-auto mb-8 band max-w-site lg:mb-16 xl:mb-20">
      <div class="px-4 py-2 mx-auto band__content max-w-content-large">
        <div class="flex flex-wrap items-start justify-between reservation lg:flex-no-wrap">
          <div class="w-full mb-8 lg:w-7/12 md:pr-8">
            <div v-show="HasErrors" class="text-status-alert">
              <ul>
                <li v-for="(error, e) of reservation.errors" :key="e">{{$t("errors."+error)}}</li>
              </ul>
            </div>
            <div v-if="step === 1" ref="step-1">
              <h1 class="pb-2 mb-4 text-2xl leading-none border-b font-700 md:text-3xl lg:text-4xl lg:mb-8">{{ $t("titles.dates") }}</h1>

              <DateTimePicker fieldDateLabel="reservation.pickUpDate" fieldTimeLabel="reservation.pickUpTime" :currDate="GetPickUpDate" :currTime="GetPickUpTime" :minDate="minDates.pickup" @changeDateTime="setPickupDateTime" />
              <DateTimePicker fieldDateLabel="reservation.returnDate" fieldTimeLabel="reservation.returnTime" :currDate="GetReturnDate" :currTime="GetReturnTime" :minDate="MinReturnDate" @changeDateTime="setReturnDateTime" />

              <Locations
                fieldLabel="reservation.pickUpLocation"
                v-bind:currentLocationId="CurrentLocationId"
                v-on:changeLocation="setLocation"
              />

              <div v-show="LocationIsAirport" class="px-8 py-6">
                <DateTimePicker fieldDateLabel="reservation.flightArrivalDate" fieldTimeLabel="reservation.flightArrivalTime" :currDate="GetPickUpDate" :currTime="GetPickUpTime" :initialDateTime="reservation.pickupDateTime" v-on:changeDateTime="setFlightArrivelDateTime" />

                <div class="flex flex-wrap w-1/2 pr-4 mb-4 input">
                  <div class="input__label">
                    <div
                      class="text-xs leading-relaxed uppercase lg:text-sm font-700"
                    >{{ $t("reservation.flightNumber") }}</div>
                  </div>

                  <div class="input__field input__field--flight">
                    <input type="text" class="w-64" v-model="reservation.flight.flightNumber" />
                    <span class="inline-block w-5 h-5 fill-current icon text-brand-1 icon--plane-arrival-light">
                      <svg aria-hidden="true" focusable="false" data-prefix="fal" data-icon="plane-arrival" class="svg-inline--fa fa-plane-arrival fa-w-20" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="currentColor" d="M632 480H8c-4.42 0-8 3.58-8 8v16c0 4.42 3.58 8 8 8h624c4.42 0 8-3.58 8-8v-16c0-4.42-3.58-8-8-8zm-51.56-177.46c-19.46-20.21-45.93-35.35-76.55-43.77l-96.94-26.68L310.5 46.83c-2.19-4.23-4.15-8.58-6.18-12.89-3.14-6.64-8.97-11.67-16.05-13.62C269.1 15.05 216.78 0 216.78 0c-.28 0-10.77-.48-18.69 8.65-5.41 6.25-7.37 16.41-5.09 24.35l42.32 151.85-87.29-24.02-26.88-67.79c-2.92-7.47-9.18-13.18-16.75-15.26C59.87 65.53 61.43 65.64 57 65.64c-13.55 0-24.77 11.34-25 25.29l.29 107.22c.25 11.54 5.21 22.55 13.62 30.21l93.32 83.17c8.42 7.67 18.62 13.31 29.49 16.3l295.95 81.45c16.21 4.46 32.32 6.73 47.88 6.73 13.79 0 27.15-1.79 39.71-5.33 30.87-8.69 49.14-23.15 54.31-43 5.17-19.85-3.61-41.76-26.13-65.14zm-4.86 57.07c-2.01 7.72-13.67 15.1-32.01 20.26-30.85 8.68-60.03 1.41-70.42-1.45L177.2 296.97c-6.03-1.66-11.72-4.81-16.43-9.1L67.45 204.7c-1.93-1.76-3.11-4.46-3.17-7.23l-.1-97.57 28.35 7.8 31.54 79.72 157.85 43.44-54.51-194.65 48.8 13.98 108.76 209.03 110.44 30.4c25.08 6.9 46.51 19.04 61.99 35.11 13.64 14.18 20.28 26.88 18.18 34.88z"></path></svg>
                    </span>
                    <div class="required-label" v-if="!reservation.flight.flightNumber || reservation.flight.flightNumber === ''">{{ $t("validation.requiredLabel") }}</div>
                  </div>
                </div>
              </div>

              <div class="required-label required-label--inline" v-if="!CurrentLocationId">{{ $t("validation.requiredLabel") }}</div>

              <div class="flex justify-between pt-6 mt-4 border-t border-grey-200">
                <span></span>

                <button @click.prevent="next()" v-show="showNextButton" class="button button--brand-1">
                  {{ $t("reservation.next") }}
                  <i class="ml-1 icon msfas msfas-angle-right"></i>
                </button>
                <span v-show="!showNextButton" class="button button--disabled outline" @mouseover="softFeedback(1)" @click="hardFeedback(1)">
                  {{ $t("reservation.next") }}
                  <i class="ml-1 icon msfas msfas-angle-right"></i>
                </span>
              </div>

            </div>
            <div v-if="step === 2" ref="step-2">
              <AvailableCars
                fieldLabel="reservation.availableScubaSuits"
                v-bind:reservation="reservation"
                v-on:changeCar="setCar"
                v-on:rentCar="rentCar"
              />

              <div class="flex justify-between pt-6 mt-4 border-t border-grey-200">
                <button
                  @click.prevent="prev()"
                  class="button button--brand-2 outline"
                >{{ $t("reservation.previous") }}</button>

                <button @click.prevent="next()" v-show="showNextButton" class="button button--brand-1">
                  {{ $t("reservation.next") }}
                  <i class="ml-1 icon msfas msfas-angle-right"></i>
                </button>
                <span v-show="!showNextButton" class="button button--disabled outline" @mouseover="softFeedback(2)" @click="hardFeedback(2)">
                  {{ $t("reservation.next") }}
                  <i class="ml-1 icon msfas msfas-angle-right"></i>
                </span>
              </div>


            </div>
            <div v-if="step === 3" ref="step-3">
              <AdditionalCharges
                fieldLabel="reservation.additionalCharges"
                v-bind:reservation="reservation"
                v-on:setAdditionalCharges="setAdditionalCharges"
                v-on:setAvailableAdditionalCharges="setAvailableAdditionalCharges"
                v-on:setAdditionalChargesPrices="setAdditionalChargesPrices"
                v-on:setErrors="setErrors"
              />
              <div class="flex justify-between pt-6 mt-4 border-t border-grey-200">
                <button
                  @click.prevent="prev()"
                  class="button button--brand-2 outline"
                >{{ $t("reservation.previous") }}</button>

                <button @click.prevent="next()" v-show="showNextButton" class="button button--brand-1">
                  {{ $t("reservation.next") }}
                  <i class="ml-1 icon msfas msfas-angle-right"></i>
                </button>
                <span v-show="!showNextButton" class="button button--disabled outline" @mouseover="softFeedback(3)" @click="hardFeedback(3)">
                  {{ $t("reservation.next") }}
                  <i class="ml-1 icon msfas msfas-angle-right"></i>
                </span>
              </div>
            </div>
            <div v-if="step === 4" ref="step-4">
              <Contact fieldLabel="reservation.contactInformation" :currentContact="reservation.contact" v-on:changeContact="setContact" />
              <h1 class="pb-2 mt-4 mb-4 text-2xl leading-none border-b font-700 md:text-3xl lg:text-1xl lg:mb-8">{{ $t("titles.terms") }}</h1>
              <div class="input__field ">
                  <label class="label">
                    <input type="checkbox" v-model="reservation.agreeWithTerms" />
                    <span><a
                      :href="reservation.termsLink"
                      target="_blank"
                      rel="noopener noreferrer"
                    >{{ $t("reservation.agreeWithTerms") }}</a></span>
                  </label>
              </div>
              <div class="flex justify-between pt-6 mt-4 border-t border-grey-200">
                  <button
                    @click.prevent="prev()"
                    class="button button--brand-2 outline"
                  >{{ $t("reservation.previous") }}</button>

                  <button @click.prevent="next()" v-show="showNextButton" class="button button--brand-1">
                    {{ $t("reservation.next") }}
                    <i class="ml-1 icon msfas msfas-angle-right"></i>
                  </button>

                  <span v-show="!showNextButton" class="button button--disabled outline" @mouseover="softFeedback(4)" @click="hardFeedback(4)" >
                    {{ $t("reservation.next") }}
                    <i class="ml-1 icon msfas msfas-angle-right"></i>
                  </span>
                </div>
            </div>
            <div v-if="step === 5" ref="step-5">
              <h1 class="pb-2 mb-4 text-2xl leading-none border-b font-700 md:text-3xl lg:text-4xl lg:mb-8">{{ $t("titles.payment") }}</h1>
              <PaymentMethods
                fieldLabel="reservation.paymentMethods"
                :reservation="reservation"
                :id="GetConfirmedReservationId"
              />
              <div class="flex justify-between pt-6 mt-4 border-t border-grey-200">
                <button
                  @click.prevent="prev()"
                  class="button button--brand-2 outline"
                >{{ $t("reservation.previous") }}</button>

                <button @click.prevent="next()" v-show="showNextButton" class="button button--brand-1">
                  {{ $t("reservation.next") }}
                  <i class="ml-1 icon msfas msfas-angle-right"></i>
                </button>
                <span v-show="!showNextButton" class="button button--disabled outline">
                  {{ $t("reservation.next") }}
                  <i class="ml-1 icon msfas msfas-angle-right"></i>
                </span>
              </div>
            </div>
          </div>

          <div class="w-full lg:w-5/12 reservation__summary">
            <ReservationSummary :reservation="reservation" :step="step" v-on:next="next()" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* global reservationConfiguration */

import AdditionalCharges from "@/components/AdditionalCharges"
import AvailableCars from "@/components/AvailableCars"
import Contact from "@/components/Contact"
import DateTimePicker from "@/components/DateTimePicker"
import Locations from "@/components/Locations"
import PaymentMethods from "@/components/PaymentMethods"
import Progress from "@/components/Progress"
import ReservationSummary from "@/components/ReservationSummary"
import CaagApi from "@/mixins/CaagApi"
import DateTimeHelper from "@/mixins/DateTimeHelper"
import ReservationHelper from "@/mixins/ReservationHelper"
import { mapGetters } from 'vuex'

export default {
  name: "ReservationScuba",
  mixins: [DateTimeHelper, ReservationHelper, CaagApi],
  components: {
    AdditionalCharges,
    AvailableCars,
    Contact,
    DateTimePicker,
    Locations,
    PaymentMethods,
    Progress,
    ReservationSummary
  },
  data: () => ({
    season: null,
    minDates: {
      pickup: null,
      return: null
    },
  }),
  watch: {
      confirmedReservation: {
        deep: true,
        handler() {
          if ( this.confirmedReservation.id ) {
            this.reservation.id = this.confirmedReservation.id
          }
        }
      },
  },
  computed: {
    ...mapGetters([
      'step',
      'reservation',
      'dates',
      'type',
      'bookNowUrl'
    ]),
    CurrentLocationId: function() {
      if (this.reservation.location) {
        return this.reservation.location.id
      } else {
        return null
      }
    },
    CurrentCarId: function() {
      if (this.reservation.car) {
        return this.reservation.car.id
      } else {
        return null
      }
    },
    HasErrors: function() {
      return this.errors.length > 0
    },
    GetPickUpDate: function() {
      if ( !this.dates.pickupDate ) {
       this.setPickupDateTime({date:new Date(),time:"12:00"})
      }
      return this.dates.pickupDate
    },
    GetPickUpTime: function() {
      if ( !this.dates.pickupTime ) {
        this.setPickupDateTime({date:new Date(),time:"12:00"})
      }
      return this.dates.pickupTime
    },
    GetReturnDate: function() {
      if (! this.dates.returnDate ) {
        this.setReturnDateTime({date:new Date(),time:"12:00"})
      
      }
      return this.dates.returnDate
    },
    GetReturnTime: function() {
      if ( !this.dates.returnTime ) {
        this.setReturnDateTime({date:new Date(),time:"12:00"})
        
      }
      return this.dates.returnTime
    },
    MinReturnDate: function() {
      let minimalRentalPeriod = null
      if ( this.season && this.season.minimal_rental_period) {
        minimalRentalPeriod = this.season.minimal_rental_period
      }
      return this.minReservationReturnDate(this.reservation.pickupDateTime, minimalRentalPeriod)
    },
    GetThankYouUrl: function() {
      if ( window.reservationConfiguration ) {
        if ( reservationConfiguration.thankYouUrl ) {
          return reservationConfiguration.thankYouUrl
        }
      }
      return null
    },
    GetConfirmedReservationId: function() {
      if ( this.confirmedReservation && this.confirmedReservation.id ) {
        return this.confirmedReservation.id
      }
      return null
    },
    showNextButton: function() {
      return (this.step === 1 && this.Step1IsValid) || (this.step === 2 && this.Step2IsValid) || (this.step === 3 && this.Step3IsValid) || (this.step === 4 && this.Step4IsValid) || (this.step === 5 && this.Step5IsValid)
    }
  },
  methods: {
    checkReservationState() {
      this.$store.commit('checkState')
      if ( this.reservation.reset ) {
        this.$store.commit('reset')
        this.$router.push({ path: `${this.reservation.basePath}/reservation/1` })
        return false
      }
      return true
    },
    prev() {
      if ( this.checkReservationState() ) {
        if ( document.querySelector('#app') ) {
          document.querySelector('#app').scrollIntoView(true)
        }
        this.$store.commit('step', this.step - 1)
        this.$router.push({ path: `${this.reservation.basePath}/reservation/${this.step}` })
        this.removeFeedback()
      }
    },
    async next() {
      if ( this.checkReservationState() ) {
        let success = true
        if ( document.querySelector('#app') ) {
          document.querySelector('#app').scrollIntoView(true)
        }
        if ( this.step === 4 ) {
          if ( !await this.book() ) {
            this.reservation.errors = []
            this.reservation.errors.push("reservationConfirmationFault")
            success = false
          }
        }
        if (this.step === 1) {
          this.reservation.car = null
        }
        if ( success ) {
          this.$store.commit('step', this.step + 1)
          this.$router.push({ path: `${this.reservation.basePath}/reservation/${this.step}` })
          this.removeFeedback()
        }
      }
    },
    async book() {
      return await this.confirmReservation(this.reservation)
    },
    setErrors(val) {
      this.reservation.errors = []
      for (var i = 0; i < val.length; i++) {
        if (this.reservation.errors.includes(val[i]) === false) {
          this.reservation.errors.push(val[i])
        }
      }
    },
    setPickupDateTime(val) {      
      this.dates.pickupTime = val.time
      if ( val.date ) {
        this.dates.pickupDate = this.getDateFormat(val.date)
        this.reservation.pickupDateTime = this.getDateTimeAPIFormat(val.date, val.time)
        this.getSeasons(this.reservation.pickupDateTime)

        if ( this.reservation.returnDateTime ) {
          if ( this.MinReturnDate > this.getDateAsJSDateObject(this.dates.returnDate))  {
            this.dates.returnDate = this.getDateFormat(this.MinReturnDate)
          }
        }
        else {
          this.setReturnDateTime(this.MinReturnDate)
        }
      }
      else {
        this.dates.pickupDate = val.date
      }
    },
    setReturnDateTime(val) {
      this.dates.returnTime =val.time
      if ( val.date ) {
        this.dates.returnDate = this.getDateFormat(val.date)
        this.reservation.returnDateTime = this.getDateTimeAPIFormat(val.date, val.time)
      }
      else {
        this.dates.returnDate = val.date
      }
    },
    setLocation(val) {
      this.reservation.location = val.pickupLocation
      this.reservation.returnLocation = val.returnLocation
    },
    setFlightArrivelDateTime(val) {
      if ( val.date ) {
        this.reservation.flight.arrivalDateTime = this.getDateTimeAPIFormat(val.date, val.time)
      }
    },
    setCar(val) {
      this.reservation.car = val
    },
    rentCar(val) {
      this.setCar(val)
      if (this.Step2IsValid) {
        this.next()
      }
    },
    setAvailableAdditionalCharges(val) {
      this.reservation.availableAdditionalCharges = val
    },
    setAdditionalCharges(val) {
      this.reservation.additionalCharges = val
    },
    setAdditionalChargesPrices(val) {
      this.reservation.additionalChargesPrices = val
    },
    setDiscountCode(val) {
      this.reservation.discountCode = val
    },
    setContact(val) {
      this.reservation.contact = val
    },
    setStep(step) {
      this.$store.commit('check')
      this.$store.commit('step', step)      
      this.$router.push({ path: `${this.reservation.basePath}/reservation/${this.step}` })
    },
    softFeedback(step) {
      let ref = this.$refs[`step-${step}`]
      if (ref) {
        ref.classList.add('soft-feedback')
      }
    },
    hardFeedback(step) {
      let ref = this.$refs[`step-${step}`]
      if (ref) {
        ref.classList.add('hard-feedback')
      }
    },
    removeFeedback() {
      document.querySelectorAll('.soft-feedback').forEach((el) => { el.classList.remove('soft-feedback') })
      document.querySelectorAll('.hard-feedback').forEach((el) => { el.classList.remove('hard-feedback') })
    },
    cleanupReservation() {
      this.$store.reservation = null
      this.$store.step = 1
    }
  },
  mounted: function() {
    this.$store.dispatch('setType')
    switch ( document.querySelector('html').attributes['lang'].value ) {
      case "nl-NL":
        this.$i18n.locale = 'nl'
        this.reservation.basePath = this.bookNowUrl
        this.reservation.termsLink = '/nl/algemene-voorwaarden'
        break
      case "de":
        this.$i18n.locale = 'de'
        this.reservation.basePath = this.bookNowUrl
        this.reservation.termsLink = '/de/allgemeine-geschaeftsbedingungen'
        break
      default:
        this.$i18n.locale = 'en'
        this.reservation.basePath = this.bookNowUrl
        this.reservation.termsLink = '/terms-conditions'
        break
    }

    if ( this.checkReservationState() ) {
      this.minDates.pickup = this.minReservationDate()
      this.minDates.return = this.minReservationDate()

      if ( this.dates.pickupDate || this.dates.pickupTime || this.dates.returnDate || this.dates.returnTime) {
        //
      } else {
        this.$store.commit('reset')
      }

      if (this.dates.pickupDate && this.dates.pickupTime) {
        this.reservation.pickupDateTime = this.getDateTimeAPIFormat(this.dates.pickupDate, this.dates.pickupTime)
      }

      if (this.dates.returnDate !== "" && this.dates.returnTime !== "") {
        this.reservation.returnDateTime = this.getDateTimeAPIFormat(this.dates.returnDate, this.dates.returnTime)
      }
    }
  },
  beforeRouteUpdate (to, from, next) {
    if (to.params && to.params.stepID && !isNaN(to.params.stepID)) {      
      this.$store.commit('step', parseInt(to.params.stepID))
    }
    next()
  }
}
</script>
